import { useState } from "react";
import { Link, useMatch } from "react-router-dom";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  Building2,
  CalendarDays,
  Database,
  FolderOpen,
  Gem,
  Headset,
  LayoutDashboard,
  Megaphone,
  Phone,
  PhoneCall,
  Server,
  Settings,
  Table,
  Target,
  Users,
} from "lucide-react";

import ACL_RELATIONSHIPS from "acl-relationships";
import CONFIG from "Config";
import { SidebarNavCollectionSkeleton } from "components/AppSidebar/SidebarNavCollectionSkeleton";
import {
  SidebarNavItem,
  SidebarSection,
} from "components/sidebar/sections/SidebarNav";
import {
  SIDEBAR_GROUP_LABEL_CLASSES,
  SIDEBAR_MENU_BUTTON_CLASSES,
} from "components/sidebar/sidebarShell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import {
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import {
  AGENT_DASHBOARD_ACLS,
  CALLS_ACLS,
  CALL_DASHBOARD_ACLS,
  CAMPAIGNS_ACLS,
  CASES_ACLS,
  CONTACTS_ACLS,
  DATA_ACLS,
  DIAL_SESSIONS_ACLS,
  ENTITIES_ACLS,
  MACHINES_ACLS,
  REVENUE_OPPORTUNITIES_ACLS,
  SWOOGO_ATTENDANCES_ACLS,
  SWOOGO_EVENTS_ACLS,
  SWOOGO_REGISTRANTS_ACLS,
  SWOOGO_SESSIONS_ACLS,
} from "constants/NavigationAcls";
import { upperFirst } from "helpers/string";
import useAuthorized from "hooks/useAuthorized";
import { useRuvixxFlags } from "providers/RuvixxFlagsProvider";
import NamedIcon from "components/icons/NamedIcon";
import { useAvailableLeadSources } from "routes/leads/leadSourceRegistry";
import { useReadableSwoogoSections } from "routes/swoogo/swoogoSections";
import {
  SwoogoSyncButton,
  SwoogoSyncNote,
} from "routes/swoogo/SwoogoSyncControl";
import useSwoogoConnected from "routes/swoogo/useSwoogoConnected";
import CustomTableService from "services/CustomTable";

const SWOOGO_READ_ACLS = [
  ...SWOOGO_EVENTS_ACLS,
  ...SWOOGO_REGISTRANTS_ACLS,
  ...SWOOGO_SESSIONS_ACLS,
  ...SWOOGO_ATTENDANCES_ACLS,
];

const NAV_STALE_TIME = 60_000;
// A stale session can return 403 for these background requests on an unrelated
// page. The groups then render empty, so the global forbidden modal is suppressed.
const NAV_REQUEST_OPTIONS = { suppressForbiddenModal: true };

const SidebarSubNavItem = ({ item, onNavigate }) => {
  const isActive = !!useMatch({ path: item.to, end: false });

  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton
        isActive={isActive}
        className={SIDEBAR_MENU_BUTTON_CLASSES}
        onClick={onNavigate}
        render={<Link to={item.to} />}
      >
        {item.icon}
        <span>{item.label}</span>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
};

// A data-driven group (custom tables, lead accounts). When the rail is collapsed
// to icons, every row would show the same icon, so the group renders as a single
// icon that opens a labelled menu instead.
//
// `subItems` opens an accordion under the group's rows while the route is inside
// `basePath`, and replaces `items` in the collapsed menu so every section stays
// reachable. `headerAction` sits beside the manage gear and `subFooter` closes
// the sub-list, both only while the accordion is open. The group's own rows are
// not highlighted then, because the active sub-item carries the state.
const SidebarNavCollection = ({
  label,
  icon,
  basePath,
  items,
  subItems = [],
  headerAction,
  subFooter,
  emptyLabel,
  manageTo,
  manageLabel,
  manageTooltip,
  isLoading = false,
}) => {
  const { state, isMobile, setOpenMobile } = useSidebar();
  const isActive = !!useMatch({ path: basePath, end: false });
  const [menuOpen, setMenuOpen] = useState(false);
  const isAccordionOpen = isActive && subItems.length > 0;
  const menuItems = subItems.length > 0 ? subItems : items;
  const hasItems = menuItems.length > 0;

  // The mobile rail is the primitive's Sheet, which is always full width.
  if (state === "expanded" || isMobile) {
    if (isLoading) {
      return (
        <SidebarNavCollectionSkeleton label={label} hasManage={!!manageTo} />
      );
    }

    return (
      <SidebarGroup>
        <SidebarGroupLabel className={SIDEBAR_GROUP_LABEL_CLASSES}>
          {label}
        </SidebarGroupLabel>
        {manageTo && (
          <Tooltip>
            <TooltipTrigger
              render={
                <SidebarGroupAction
                  render={<Link to={manageTo} />}
                  aria-label={manageTooltip}
                >
                  <Settings />
                </SidebarGroupAction>
              }
            />
            <TooltipContent side="right">{manageTooltip}</TooltipContent>
          </Tooltip>
        )}
        {isAccordionOpen && headerAction && (
          <div className="tw:absolute tw:top-3.5 tw:right-9 tw:z-10">
            {headerAction}
          </div>
        )}
        <SidebarMenu>
          {items.map(item => (
            <SidebarNavItem
              key={item.key}
              to={item.to}
              end={subItems.length > 0}
              icon={item.icon}
              tooltip={item.label}
            >
              {item.label}
            </SidebarNavItem>
          ))}
          {isAccordionOpen && (
            <SidebarMenuItem>
              <SidebarMenuSub>
                {subItems.map(item => (
                  <SidebarSubNavItem
                    key={item.key}
                    item={item}
                    onNavigate={() => isMobile && setOpenMobile(false)}
                  />
                ))}
                {subFooter && (
                  <SidebarMenuSubItem className="tw:px-2 tw:py-1">
                    {subFooter}
                  </SidebarMenuSubItem>
                )}
              </SidebarMenuSub>
            </SidebarMenuItem>
          )}
          {manageTo && (
            <SidebarNavItem
              to={manageTo}
              icon={<Settings />}
              tooltip={manageLabel}
            >
              {manageLabel}
            </SidebarNavItem>
          )}
        </SidebarMenu>
      </SidebarGroup>
    );
  }

  return (
    <SidebarGroup>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <Tooltip>
              <TooltipTrigger
                render={
                  <DropdownMenuTrigger
                    render={
                      <SidebarMenuButton
                        isActive={isActive}
                        className={SIDEBAR_MENU_BUTTON_CLASSES}
                      />
                    }
                    aria-label={label}
                  >
                    {icon}
                  </DropdownMenuTrigger>
                }
              />
              <TooltipContent side="right">{label}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent
              side="right"
              align="start"
              className="tw:min-w-56"
            >
              <DropdownMenuGroup>
                <div className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:pr-1">
                  <DropdownMenuLabel>{label}</DropdownMenuLabel>
                  {manageTo && (
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <Link
                            to={manageTo}
                            aria-label={manageTooltip}
                            onClick={() => setMenuOpen(false)}
                            className="tw:flex tw:size-6 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-sm tw:text-muted-foreground tw:hover:bg-accent tw:hover:text-accent-foreground tw:[&>svg]:size-4"
                          >
                            <Settings />
                          </Link>
                        }
                      />
                      <TooltipContent side="right">
                        {manageTooltip}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                {isLoading &&
                  Array.from({ length: 3 }, (_, index) => (
                    <SidebarMenuSkeleton key={index} showIcon />
                  ))}
                {hasItems &&
                  menuItems.map(item => (
                    <DropdownMenuItem
                      key={item.key}
                      render={<Link to={item.to} />}
                    >
                      {item.icon}
                      <span className="tw:truncate">{item.label}</span>
                    </DropdownMenuItem>
                  ))}
                {!isLoading && !hasItems && !manageTo && (
                  <div className="tw:px-1.5 tw:py-1 tw:text-sm tw:text-muted-foreground">
                    {emptyLabel}
                  </div>
                )}
              </DropdownMenuGroup>
              {manageTo && (
                <>
                  {hasItems && <DropdownMenuSeparator />}
                  <DropdownMenuItem render={<Link to={manageTo} />}>
                    <Settings />
                    {manageLabel}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarGroup>
  );
};

const CustomTablesGroup = () => {
  const canAccessCustomTables = useAuthorized(
    [
      ACL_RELATIONSHIPS.customTable.readData,
      ACL_RELATIONSHIPS.customTable.readSchema,
      ACL_RELATIONSHIPS.customTable.editSchema,
    ],
    { partialMatch: true }
  );
  const canManageCustomTables = useAuthorized([
    ACL_RELATIONSHIPS.customTable.editSchema,
  ]);

  const { data, isPending } = useQuery({
    queryKey: ["custom-tables-nav"],
    queryFn: () =>
      CustomTableService.list(
        { per_page: 100, sort_by: "created_at", sort_dir: "desc" },
        NAV_REQUEST_OPTIONS
      ),
    enabled: canAccessCustomTables,
    staleTime: NAV_STALE_TIME,
  });

  if (!canAccessCustomTables) {
    return null;
  }

  const items = (data?.data ?? []).map(table => ({
    key: table.slug,
    to: `/custom_tables/${table.slug}`,
    label: upperFirst(table.display_name),
    icon: <NamedIcon name={table.icon_name} />,
  }));

  return (
    <SidebarNavCollection
      label="Custom Tables"
      icon={<Table />}
      basePath="/custom_tables"
      items={items}
      emptyLabel="No tables yet"
      manageTo={canManageCustomTables ? "/settings/custom_tables" : null}
      manageLabel="Manage Tables"
      manageTooltip="Manage Custom Tables"
      isLoading={isPending}
    />
  );
};

const LeadsGroup = () => {
  const readableSources = useAvailableLeadSources("readAcl");
  const canManageAny = useAvailableLeadSources("manageAcl").length > 0;

  const results = useQueries({
    queries: readableSources.map(source => ({
      queryKey: ["leads-nav", source.key],
      queryFn: () =>
        source.listConnections(
          { per_page: 100, sort_by: "name", sort_dir: "asc" },
          NAV_REQUEST_OPTIONS
        ),
      staleTime: NAV_STALE_TIME,
    })),
  });

  if (readableSources.length === 0) {
    return null;
  }

  const items = readableSources.flatMap((source, index) => {
    const Icon = source.icon;
    return (results[index]?.data?.data ?? []).map(connection => ({
      key: `${source.key}-${connection.id}`,
      to: `/leads/${source.key}/${connection.id}`,
      label: connection.name,
      icon: Icon && <Icon />,
    }));
  });

  return (
    <SidebarNavCollection
      label="Meta"
      icon={<Target />}
      basePath="/leads"
      items={items}
      emptyLabel="No connected accounts"
      manageTo={canManageAny ? "/settings/integrations/meta" : null}
      manageLabel="Manage Accounts"
      manageTooltip="Manage Meta Accounts"
      isLoading={results.some(result => result.isPending)}
    />
  );
};

const SwoogoGroup = () => {
  const { isConnected, isLoading, accountName } = useSwoogoConnected();
  const canManage = useAuthorized([ACL_RELATIONSHIPS.swoogo.manageConnection]);
  const canReadAny = useAuthorized(SWOOGO_READ_ACLS, { partialMatch: true });
  const sections = useReadableSwoogoSections();

  if (!isConnected || !canReadAny) {
    return null;
  }

  const subItems = sections.map(({ key, label, icon: Icon }) => ({
    key,
    label,
    icon: <Icon />,
    to: `/swoogo/${key}`,
  }));

  const items = [
    {
      key: "swoogo",
      to: "/swoogo",
      label: accountName ?? "Swoogo",
      icon: <CalendarDays />,
    },
  ];

  return (
    <SidebarNavCollection
      label="Swoogo"
      icon={<CalendarDays />}
      basePath="/swoogo"
      items={items}
      subItems={subItems}
      headerAction={<SwoogoSyncButton />}
      subFooter={<SwoogoSyncNote />}
      emptyLabel="No connected account"
      manageTo={canManage ? "/settings/integrations/swoogo" : null}
      manageLabel="Manage Account"
      manageTooltip="Manage Swoogo Account"
      isLoading={isLoading}
    />
  );
};

export function SidebarNavItems() {
  const {
    enable_leads_feature,
    enable_meta_account,
    enable_swoogo_integration,
  } = useRuvixxFlags();

  return (
    <>
      <SidebarGroup>
        <SidebarMenu>
          <SidebarNavItem
            to="/campaigns"
            icon={<Megaphone />}
            tooltip="Campaigns"
            permissions={CAMPAIGNS_ACLS}
          >
            Campaigns
          </SidebarNavItem>
          <SidebarNavItem
            to="/entities"
            icon={<Building2 />}
            tooltip="Entities"
            permissions={ENTITIES_ACLS}
          >
            Entities
          </SidebarNavItem>
          <SidebarNavItem
            to="/contacts"
            icon={<Users />}
            tooltip="Contacts"
            permissions={CONTACTS_ACLS}
          >
            Contacts
          </SidebarNavItem>
          <SidebarNavItem
            to="/revenue_opportunities"
            icon={<Gem />}
            tooltip="Revenue"
            permissions={REVENUE_OPPORTUNITIES_ACLS}
          >
            Revenue
          </SidebarNavItem>
          <SidebarNavItem
            to="/data"
            icon={<Database />}
            tooltip="Data"
            permissions={DATA_ACLS}
          >
            Data
          </SidebarNavItem>
        </SidebarMenu>
      </SidebarGroup>

      <SidebarSection
        label="Call Center"
        conditions={[CONFIG.IS_PHONE_ENABLED]}
      >
        <SidebarNavItem
          to="/dial_sessions"
          icon={<PhoneCall />}
          tooltip="Dial Sessions"
          permissions={DIAL_SESSIONS_ACLS}
        >
          Dial Sessions
        </SidebarNavItem>
        <SidebarNavItem
          to="/call_dashboard"
          icon={<LayoutDashboard />}
          tooltip="Call Dashboard"
          permissions={CALL_DASHBOARD_ACLS}
        >
          Call Dashboard
        </SidebarNavItem>
        <SidebarNavItem
          to="/agent_dashboard"
          icon={<Headset />}
          tooltip="Call Monitoring"
          permissions={AGENT_DASHBOARD_ACLS}
        >
          Call Monitoring
        </SidebarNavItem>
        <SidebarNavItem
          to="/calls"
          icon={<Phone />}
          tooltip="Calls"
          permissions={CALLS_ACLS}
        >
          Calls
        </SidebarNavItem>
      </SidebarSection>

      <SidebarSection label="Anti-Piracy" conditions={[CONFIG.ENABLE_PIRACY]}>
        <SidebarNavItem
          to="/cases"
          icon={<FolderOpen />}
          tooltip="Cases"
          permissions={CASES_ACLS}
        >
          Cases
        </SidebarNavItem>
        <SidebarNavItem
          to="/machines"
          icon={<Server />}
          tooltip="Machines"
          permissions={MACHINES_ACLS}
        >
          Machines
        </SidebarNavItem>
      </SidebarSection>

      {enable_swoogo_integration && <SwoogoGroup />}
      {enable_leads_feature && enable_meta_account && <LeadsGroup />}
      <CustomTablesGroup />
    </>
  );
}
